import LogRocket from "logrocket";
import { storage } from "./storage";

/**
 * LogRocket integration wrapper.
 *
 * LogRocket is only initialized when VUE_APP_LOGROCKET_ID is set, so local dev
 * without the env var is a no-op. All input values are masked by default
 * (inputSanitizer) because this app collects PII (customer name, WhatsApp
 * number, QRIS payment details). Non-input PII must be redacted in templates
 * with `class="data-private"`.
 */

const APP_ID = process.env.VUE_APP_LOGROCKET_ID;

let initialized = false;

export function initLogRocket() {
  if (!APP_ID) {
    return;
  }

  LogRocket.init(APP_ID, {
    release: process.env.VUE_APP_VERSION,
    // NOTE: input masking is currently OFF, so replays capture input values
    // (incl. customer name / WA number) in plain text. Re-enable inputSanitizer
    // or add `data-private` to specific fields before this scales up.
    dom: {
      inputSanitizer: false,
      textSanitizer: false,
    },
  });

  initialized = true;
}

/**
 * Associate the current session with the customer. Values fall back to storage
 * so callers that don't have the data on hand can still identify.
 *
 * @param {{ name?: string, waNumber?: string }} [overrides]
 */
export function identifyLogRocket(overrides = {}) {
  if (!initialized) {
    return;
  }

  const customerObj = storage.getCustomer();
  let storedName = "";
  let storedWa = "";

  if (customerObj) {
    if (typeof customerObj === "object") {
      storedName = customerObj.name || "";
      storedWa = customerObj.phone || "";
    } else if (typeof customerObj === "string") {
      storedName = customerObj;
    }
  }

  const name = overrides.name || storedName;
  const waNumber = overrides.waNumber || storage.getWaNumber() || storedWa;

  if (!name && !waNumber) {
    return;
  }

  const traits = {};
  if (name) traits.name = name;
  if (waNumber) traits.waNumber = waNumber;

  LogRocket.identify(waNumber || name, traits);
}

/**
 * Pinia plugin: forward store actions into the LogRocket timeline so replays
 * carry state-change context. Registered via `pinia.use(logRocketPiniaPlugin)`.
 */
export function logRocketPiniaPlugin({ store }) {
  store.$onAction(({ name, store: actionStore, after, onError }) => {
    if (!initialized) {
      return;
    }
    after(() => {
      LogRocket.log(`[pinia] ${actionStore.$id}.${name}`);
    });
    onError((error) => {
      LogRocket.captureException(error);
    });
  });
}

export default LogRocket;
